import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, X } from "lucide-react";
import api from "../../lib/api";

const computeAge = (dob) => {
    if (!dob) return null;
    const b = new Date(dob);
    if (isNaN(b.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - b.getFullYear();
    const m = now.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
    return age >= 0 && age < 100 ? age : null;
};

export default function AdminLineup() {
    const { num } = useParams();
    const [game, setGame] = useState(null);
    const [teams, setTeams] = useState([]);
    const [players, setPlayers] = useState([]);
    const [att, setAtt] = useState([]);
    const [stats, setStats] = useState([]);

    const load = () => {
        api.get(`/games/${num}`).then(r => setGame(r.data));
        api.get("/teams").then(r => setTeams(r.data));
        api.get("/players").then(r => setPlayers(r.data));
        api.get(`/attendance/${num}`).then(r => setAtt(r.data));
        api.get(`/player_stats/${num}`).then(r => setStats(r.data));
    };
    useEffect(() => { load(); }, [num]); // eslint-disable-line

    if (!game) return <div className="text-white/50">Chargement...</div>;
    const teamMap = Object.fromEntries(teams.map(t => [t.id, t]));

    const togglePresent = async (p, present) => {
        await api.post("/attendance", { game_num: parseInt(num, 10), team_id: p.team_id, player_id: p.id, present });
        load();
    };
    const updateStat = async (p, field, val) => {
        const cur = stats.find(s => s.player_id === p.id) || { goals: 0, assists: 0, pim: 0 };
        await api.post("/player_stats", { game_num: parseInt(num, 10), team_id: p.team_id, player_id: p.id, goals: cur.goals, assists: cur.assists, pim: cur.pim, [field]: parseInt(val || 0, 10) });
        load();
    };

    const Side = ({ tid, label }) => {
        const list = players.filter(p => p.team_id === tid).sort((a,b) => a.number - b.number);
        const team = teamMap[tid];
        return (
            <div className="glass-card p-4">
                <div className="font-display text-2xl uppercase tracking-widest mb-3" style={{ color: team?.color || "#7FD93C" }}>{label} — {team?.name || "—"}</div>
                <div className="grid gap-2">
                    {list.length === 0 && <div className="text-white/40 text-sm">Aucun joueur.</div>}
                    {list.map(p => {
                        const a = att.find(x => x.player_id === p.id);
                        const s = stats.find(x => x.player_id === p.id) || { goals: 0, assists: 0, pim: 0 };
                        const age = computeAge(p.dob);
                        return (
                            <div key={p.id} className="py-2 border-b border-white/5" data-testid={`lineup-${p.id}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="font-display text-2xl text-[#4FE0D5] w-12 shrink-0">#{p.number}</div>
                                    <div className="flex-1 min-w-0 text-sm truncate">
                                        {p.name}
                                        {age !== null && (
                                            <span className="ml-2 text-xs text-white/50" data-testid={`age-${p.id}`}>({age} ans)</span>
                                        )}
                                        {p.position && <span className="ml-2 text-[10px] text-[#4FE0D5]/70 uppercase tracking-widest">{p.position}</span>}
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        <button onClick={() => togglePresent(p, true)} className={`p-1.5 ${a?.present ? "bg-[#7FD93C] text-black" : "bg-white/5 text-white/60"}`}><Check size={14} /></button>
                                        <button onClick={() => togglePresent(p, false)} className={`p-1.5 ${a?.present === false ? "bg-rose-500 text-white" : "bg-white/5 text-white/60"}`}><X size={14} /></button>
                                    </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-2 pl-14">
                                    <div className="flex items-center gap-1"><span className="label-mini">B</span><input type="number" min="0" className="input-field w-14 text-center" value={s.goals} onChange={e => updateStat(p, "goals", e.target.value)} /></div>
                                    <div className="flex items-center gap-1"><span className="label-mini">A</span><input type="number" min="0" className="input-field w-14 text-center" value={s.assists} onChange={e => updateStat(p, "assists", e.target.value)} /></div>
                                    <div className="flex items-center gap-1"><span className="label-mini">PIM</span><input type="number" min="0" className="input-field w-14 text-center" value={s.pim} onChange={e => updateStat(p, "pim", e.target.value)} /></div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div data-testid="admin-lineup">
            <Link to="/admin/scores" className="inline-flex items-center gap-2 text-[#4FE0D5] mb-4"><ArrowLeft size={14} /> Retour aux scores</Link>
            <h1 className="font-display text-4xl uppercase tracking-widest mb-2">Alignement & Stats</h1>
            <p className="text-white/60 text-sm mb-6">Partie #{game.game_num} — {game.category}</p>
            <div className="grid lg:grid-cols-2 gap-6">
                <Side tid={game.away_team_id} label="Visiteur" />
                <Side tid={game.home_team_id} label="Local" />
            </div>
        </div>
    );
}
